/* Sioux customizations */

/* Announcement bar: drive the prev/next arrows through the Flickity slider
   that theme.js initialises. */
function onAnnouncementArrowClick(event) {
  const arrow = event.target.closest('[data-announcement-direction]');
  if (!arrow) return;

  const nav = arrow.closest('.announcement__nav');
  if (!nav) return;

  const slider = document.getElementById(nav.dataset.slider);
  if (!slider || !window.Flickity) return;

  const flkty = window.Flickity.data(slider);
  if (!flkty) return;

  if (arrow.dataset.announcementDirection === 'prev') {
    flkty.previous();
  } else {
    flkty.next();
  }
}

function bindAnnouncementArrows() {
  document.querySelectorAll('.announcement__nav').forEach((nav) => {
    if (nav.dataset.bound === 'true') return;
    nav.dataset.bound = 'true';
    nav.addEventListener('click', onAnnouncementArrowClick);
  });
}

document.addEventListener('DOMContentLoaded', bindAnnouncementArrows);
document.addEventListener('shopify:section:load', bindAnnouncementArrows);

/* AccessGO. The SDK is never part of the page load: layout/theme.liquid only
   exposes window.bbAccessGo.load(), and the SDK is fetched when a shopper
   clicks an accessibility link (the footer button or any <a href="…#accessGo">
   in a menu, both carrying [data-accessgo-trigger]). The SDK's existing-element
   trigger opens the menu on click once it is registered. */
const ACCESSGO_TRIGGER_SELECTOR = '[data-accessgo-trigger]';
let accessGoTriggerRegistered = false;
// In-flight addTrigger() call. The SDK resolves it only after its click listener is attached, so a
// click handler that overlaps the status-change registration must await the same promise (a second
// addTrigger() would throw "already exists" before the listener is live and the replayed click is lost).
let accessGoTriggerRegistration = null;

function bbMarkAccessGoLinks(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('a[href*="#accessgo" i]').forEach((link) => link.setAttribute('data-accessgo-trigger', ''));
}

function registerAccessGoTrigger() {
  if (accessGoTriggerRegistered) return Promise.resolve(true);
  if (accessGoTriggerRegistration) return accessGoTriggerRegistration;
  if (typeof window.AgoWidget?.addTrigger !== 'function') return Promise.resolve(false);

  accessGoTriggerRegistration = window.AgoWidget.addTrigger({
    type: 'existing-element',
    selector: ACCESSGO_TRIGGER_SELECTOR,
    checkMark: { enabled: false },
  })
    .then(() => {
      accessGoTriggerRegistered = true;
    })
    .catch((error) => {
      const message = error instanceof Error ? error.message : String(error);
      if (message.includes('Trigger of type "existing-element" already exists')) {
        accessGoTriggerRegistered = true;
      } else if (!/(not loaded|not bootstrapped yet)/i.test(message)) {
        console.error('AccessGO trigger registration failed', error);
      }
    })
    .then(() => {
      accessGoTriggerRegistration = null;
      return accessGoTriggerRegistered;
    });
  return accessGoTriggerRegistration;
}

/* Click on an accessibility link before the SDK is on the page: load it, register
   the trigger, then replay the click so the SDK's own handler opens the menu.
   Once the trigger is registered the SDK handles clicks itself. */
function bbOnAccessGoTriggerClick(event) {
  if (event.bbAccessGoReplay || accessGoTriggerRegistered) return;
  const trigger = event.target.closest && event.target.closest(ACCESSGO_TRIGGER_SELECTOR);
  if (!trigger || !window.bbAccessGo) return;

  // Decided synchronously: after an await the anchor's #accessGo navigation would already have run.
  event.preventDefault();
  event.stopImmediatePropagation();
  if (trigger.getAttribute('aria-busy') === 'true') return;
  trigger.setAttribute('aria-busy', 'true');

  window.bbAccessGo
    .load()
    .then(async (readyState) => {
      if (readyState === 'hidden') return; // the shopper hid the interface earlier
      // addTrigger is attached a tick after the SDK reports its state: allow a few retries.
      let registered = false;
      for (let i = 0; i < 20 && !registered; i += 1) {
        registered = await registerAccessGoTrigger();
        if (!registered) await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      if (!registered || !trigger.isConnected) return;
      const replay = new MouseEvent('click', { bubbles: true, cancelable: true, composed: true });
      replay.bbAccessGoReplay = true;
      trigger.dispatchEvent(replay);
    })
    .catch((error) => {
      console.error('AccessGO could not be opened', error);
    })
    .finally(() => {
      trigger.removeAttribute('aria-busy');
    });
}

document.addEventListener('click', bbOnAccessGoTriggerClick, true);
window.addEventListener('ago-status-change', () => registerAccessGoTrigger());
document.addEventListener('DOMContentLoaded', () => {
  bbMarkAccessGoLinks(document);
  registerAccessGoTrigger();
});
document.addEventListener('shopify:section:load', (event) => {
  bbMarkAccessGoLinks(event.target);
  registerAccessGoTrigger();
});

/* Scroll carousel — ported from the Impact theme (ScrollArea + BaseCarousel +
   ScrollCarousel + control buttons) as autonomous custom elements (Safari has
   no customized built-ins). Markup: snippets/scroll-carousel.liquid. */

function rafThrottle(callback) {
  let requestId = null;
  let lastArgs;
  const throttled = function (...args) {
    lastArgs = args;
    if (requestId === null) {
      requestId = requestAnimationFrame(() => {
        requestId = null;
        callback.apply(this, lastArgs);
      });
    }
  };
  throttled.cancel = () => {
    cancelAnimationFrame(requestId);
    requestId = null;
  };
  return throttled;
}

// Expose the scrollbar width so the full-bleed math can subtract it.
function updateCarouselScrollbarWidth() {
  const width = window.innerWidth - document.documentElement.clientWidth;
  document.documentElement.style.setProperty('--carousel-scrollbar-width', `${width > 0 ? width : 0}px`);
}
updateCarouselScrollbarWidth();
window.addEventListener('resize', rafThrottle(updateCarouselScrollbarWidth));

// Dispatches scroll:edge-nearing / scroll:edge-leaving (detail.position
// 'start' | 'end') with a threshold + hysteresis, plus a scrollend fallback.
class ScrollArea {
  constructor(element, abortController) {
    this._element = element;
    this._allowTriggerNearingStartEvent = false;
    this._allowTriggerLeavingStartEvent = true;
    this._allowTriggerNearingEndEvent = true;
    this._allowTriggerLeavingEndEvent = false;
    this._lastScrollPosition = null;
    const signal = abortController ? abortController.signal : undefined;
    // ResizeObserver delivers an initial observation after layout, so the synchronous
    // check (a forced reflow per carousel during page load) is only the no-RO fallback.
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => this._checkIfScrollable()).observe(element);
    } else {
      this._checkIfScrollable();
    }
    element.addEventListener(
      'scroll',
      rafThrottle(() => this._onScroll()),
      { signal, passive: true },
    );
  }

  get scrollNearingThreshold() {
    return 125;
  }

  get scrollDirection() {
    if (this._element.scrollWidth > this._element.clientWidth) return 'inline';
    if (this._element.scrollHeight - this._element.clientHeight > 1) return 'block';
    return 'none';
  }

  _checkIfScrollable() {
    this._element.classList.toggle('is-scrollable', this.scrollDirection !== 'none');
  }

  _onScroll() {
    clearTimeout(this._scrollTimeout);
    const inline = this.scrollDirection === 'inline';
    const pos = inline ? Math.abs(this._element.scrollLeft) : Math.abs(this._element.scrollTop);
    if (this._lastScrollPosition == null) this._lastScrollPosition = pos;
    const direction = this._lastScrollPosition > pos ? 'start' : 'end';
    this._lastScrollPosition = pos;
    const threshold = this.scrollNearingThreshold;
    const scrollPosition = Math.round(pos);
    const scrollMinusSize = Math.round(inline ? this._element.scrollWidth - this._element.clientWidth : this._element.scrollHeight - this._element.clientHeight);

    if (direction === 'start' && this._allowTriggerNearingStartEvent && scrollPosition <= threshold) {
      this._allowTriggerNearingStartEvent = false;
      this._allowTriggerLeavingStartEvent = true;
      this._dispatch('scroll:edge-nearing', 'start');
    } else if (direction === 'end' && this._allowTriggerLeavingStartEvent && scrollPosition > threshold) {
      this._allowTriggerNearingStartEvent = true;
      this._allowTriggerLeavingStartEvent = false;
      this._dispatch('scroll:edge-leaving', 'start');
    }

    if (direction === 'end' && this._allowTriggerNearingEndEvent && scrollMinusSize <= scrollPosition + threshold) {
      this._allowTriggerNearingEndEvent = false;
      this._allowTriggerLeavingEndEvent = true;
      this._dispatch('scroll:edge-nearing', 'end');
    } else if (direction === 'start' && this._allowTriggerLeavingEndEvent && scrollMinusSize > scrollPosition + threshold) {
      this._allowTriggerNearingEndEvent = true;
      this._allowTriggerLeavingEndEvent = false;
      this._dispatch('scroll:edge-leaving', 'end');
    }

    if (window.onscrollend === undefined) {
      this._scrollTimeout = setTimeout(() => {
        this._element.dispatchEvent(new CustomEvent('scrollend'));
      }, 75);
    }
  }

  _dispatch(name, position) {
    this._element.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { position } }));
  }
}

// Indexing + prev/next/select + carousel:* events.
class BaseCarousel extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;
    if (this.items.length > 1) {
      this.addEventListener('control:prev', this.previous, { signal });
      this.addEventListener('control:next', this.next, { signal });
      this.addEventListener('control:select', this._onControlSelect, { signal });
    }
    if (this.selectedIndex === 0) {
      if (this.selectedSlide) this.selectedSlide.classList.add('is-selected');
      this._dispatchEvent('carousel:select', 0);
    } else {
      this.select(this.selectedIndex, { animate: false, force: true });
    }
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  get items() {
    if (!this._items) {
      const nodes = this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children;
      this._items = Array.from(nodes);
    }
    return this._items;
  }

  get visibleItems() {
    return this.items.filter((item) => item.offsetParent !== null);
  }

  get selectedIndex() {
    if (this._selectedIndex == null) this._selectedIndex = parseInt(this.getAttribute('initial-index'), 10) || 0;
    return this._selectedIndex;
  }

  get selectedSlide() {
    return this.items[this.selectedIndex];
  }

  get selectedIndexAmongVisible() {
    return this.visibleItems.indexOf(this.selectedSlide);
  }

  get previousSlide() {
    return this.visibleItems[Math.max(this.selectedIndexAmongVisible - 1, 0)];
  }

  get nextSlide() {
    return this.visibleItems[Math.min(this.selectedIndexAmongVisible + 1, this.visibleItems.length - 1)];
  }

  previous() {
    this.select(this.items.indexOf(this.previousSlide), { direction: 'previous' });
  }

  next() {
    this.select(this.items.indexOf(this.nextSlide), { direction: 'next' });
  }

  _onControlSelect(event) {
    this.select(event.detail.index);
  }

  select(index) {
    this._selectedIndex = index;
  }

  _dispatchEvent(name, index) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { slide: this.items[index], index } }));
  }
}

// Native scroll-snap carousel: one card per step, snap-aligned, with bleed.
class ScrollCarousel extends BaseCarousel {
  connectedCallback() {
    this._hasPendingProgrammaticScroll = false;
    super.connectedCallback();
    this._scrollArea = new ScrollArea(this, this._abortController);
    const { signal } = this._abortController;
    this.addEventListener(
      'scroll',
      rafThrottle(() => this._onCarouselScroll()),
      { signal, passive: true },
    );
    this.addEventListener('scrollend', () => this._onScrollSettled(), { signal });
    this._resetScroll();
    this._setupOverflowMeasure();
  }

  // When `overflow-from` is set, size --carousel-overflow to the tallest matching
  // descendant's overhang BELOW its positioned ancestor (e.g. the quick-add
  // overlay extending past the card) so the reserve adapts to content instead
  // of a fixed value. Re-measures on resize (wrapping changes the height).
  _setupOverflowMeasure() {
    if (!this.hasAttribute('overflow-from')) return;
    const measure = rafThrottle(() => this._measureOverflow());
    if ('ResizeObserver' in window) {
      // Initial observation runs the first measure after layout (no forced reflow).
      this._overflowObserver = new ResizeObserver(measure);
      this._overflowObserver.observe(this);
    } else {
      window.addEventListener('resize', measure, { signal: this._abortController.signal });
      this._measureOverflow();
    }
  }

  _measureOverflow() {
    let max = 0;
    for (const el of this.querySelectorAll(this.getAttribute('overflow-from'))) {
      const parent = el.offsetParent;
      const overhang = parent ? el.offsetTop + el.offsetHeight - parent.offsetHeight : el.offsetHeight;
      max = Math.max(max, overhang);
    }
    this.style.setProperty('--carousel-overflow', `${Math.ceil(Math.max(0, max))}px`);
  }

  get _rtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  get itemOffset() {
    const v = this.visibleItems;
    return v.length < 2 ? 0 : v[1].offsetLeft - v[0].offsetLeft;
  }

  select(index, options = {}) {
    const animate = options.animate !== false;
    const force = options.force === true;
    if (!this.offsetParent || this._scrollArea.scrollDirection === 'none') {
      this._selectedIndex = index;
      return;
    }
    const clamped = Math.max(0, Math.min(index, this.items.length - 1));
    const before = this.selectedIndex;
    this._selectedIndex = clamped;
    this._dispatchEvent('carousel:select', clamped);
    if (clamped !== before || force) {
      this._dispatchEvent('carousel:change', clamped);
      this._transitionTo(this.items[before], this.items[clamped], { animate });
    }
  }

  _transitionTo(fromSlide, toSlide, options = {}) {
    const animate = options.animate !== false;
    if (fromSlide) fromSlide.classList.remove('is-selected');
    if (toSlide) toSlide.classList.add('is-selected');
    if (!toSlide) return;

    const first = this.items[0];
    let scrollAmount;
    if (this._extractSlideAlign(toSlide) === 'center') {
      scrollAmount = toSlide.offsetLeft - (this.clientWidth - toSlide.clientWidth) / 2;
    } else {
      scrollAmount = toSlide.offsetLeft - (first ? first.offsetLeft : 0);
    }
    // The last slides can't be start-aligned, so clamp to the scrollable range.
    const maxScroll = Math.max(0, this.scrollWidth - this.clientWidth);
    scrollAmount = Math.max(0, Math.min(scrollAmount, maxScroll));

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const smooth = animate && !reduce;
    this._hasPendingProgrammaticScroll = animate;
    // Disable snapping for the programmatic scroll: otherwise the snap engine
    // redirects a smooth scroll-to-edge to a nearer snap point, so the last
    // card jerks back. Restored on settle, where we already sit on a snap point.
    if (smooth) this.style.scrollSnapType = 'none';
    this.scrollTo({ left: this._rtl ? -scrollAmount : scrollAmount, behavior: smooth ? 'smooth' : 'auto' });
  }

  _onCarouselScroll() {
    if (this._hasPendingProgrammaticScroll || this._scrollArea.scrollDirection === 'none') return;
    const offset = this.itemOffset;
    if (!offset) return;
    const amongVisible = Math.max(0, Math.min(Math.round(Math.abs(this.scrollLeft) / Math.abs(offset)), this.visibleItems.length - 1));
    const newIndex = this.items.indexOf(this.visibleItems[amongVisible]);
    if (newIndex !== -1 && newIndex !== this.selectedIndex) {
      this._selectedIndex = newIndex;
      this._dispatchEvent('carousel:select', newIndex);
      this._dispatchEvent('carousel:change', newIndex);
    }
  }

  _onScrollSettled() {
    this.style.scrollSnapType = '';
    for (const item of this.items) item.classList.remove('is-selected');
    if (this.selectedSlide) this.selectedSlide.classList.add('is-selected');
    this._hasPendingProgrammaticScroll = false;
    this._dispatchEvent('carousel:settle', this.selectedIndex);
  }

  _extractSlideAlign(slide) {
    return getComputedStyle(slide).scrollSnapAlign.indexOf('center') !== -1 ? 'center' : 'start';
  }

  // Pin to the logical start so the browser can't restore an offset on reload.
  _resetScroll() {
    const pin = () => {
      this.scrollLeft = this._rtl ? this.scrollWidth : 0;
    };
    pin();
    requestAnimationFrame(pin);
    this._selectedIndex = 0;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._overflowObserver) this._overflowObserver.disconnect();
  }
}

// Autonomous control buttons: wrap a real <button>, dispatch control:prev/next
// on the carousel named by aria-controls, and hide as it nears the matching edge.
class ScrollControlButton extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;
    this.button = this.querySelector('button');
    this._controlled = this._controlledElement();
    if (this.button) {
      this.button.addEventListener('click', () => this._onClick(), { signal });
    }
    if (this._controlled) {
      this._controlled.addEventListener('scroll:edge-nearing', (event) => this._onEdge(true, event), { signal });
      this._controlled.addEventListener('scroll:edge-leaving', (event) => this._onEdge(false, event), { signal });
    }
  }

  disconnectedCallback() {
    if (this._abortController) this._abortController.abort();
  }

  get position() {
    return 'start';
  }

  get command() {
    return 'control:prev';
  }

  _controlledElement() {
    const id = (this.button && this.button.getAttribute('aria-controls')) || this.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  _onClick() {
    if (this._controlled) {
      this._controlled.dispatchEvent(new CustomEvent(this.command, { bubbles: true, cancelable: true }));
    }
  }

  _onEdge(nearing, event) {
    if (event.detail.position !== this.position) return;
    this._setHidden(nearing);
  }

  _setHidden(state) {
    if (!this.button) return;
    // Move focus off a disappearing button without scrolling it into view.
    if (state && document.activeElement === this.button) {
      const fallback = this._siblingButton() || this._controlled;
      if (fallback) fallback.focus({ preventScroll: true });
    }
    this.button.disabled = state;
    this.button.hidden = state;
  }

  _siblingButton() {
    if (!this.parentElement) return null;
    const buttons = this.parentElement.querySelectorAll('.scroll-carousel__arrow');
    for (const candidate of buttons) {
      if (candidate !== this.button && !candidate.disabled && !candidate.hidden) return candidate;
    }
    return null;
  }
}

class ScrollPrevButton extends ScrollControlButton {
  get position() {
    return 'start';
  }
  get command() {
    return 'control:prev';
  }
}

class ScrollNextButton extends ScrollControlButton {
  get position() {
    return 'end';
  }
  get command() {
    return 'control:next';
  }
}

// Mirrors the observed carousel's scroll position onto a --scroll-progress
// (0–1) custom property. Reflects "portion seen" (Impact behaviour).
class ScrollProgress extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const observed = this.observedElement;
    if (!observed) return;
    const update = rafThrottle(() => this._update());
    observed.addEventListener('scroll', update, { signal: this._abortController.signal, passive: true });
    if ('ResizeObserver' in window) {
      // Initial observation runs the first update after layout (no forced reflow).
      this._ro = new ResizeObserver(update);
      this._ro.observe(observed);
    } else {
      this._update();
    }
  }

  disconnectedCallback() {
    if (this._abortController) this._abortController.abort();
    if (this._ro) this._ro.disconnect();
  }

  get observedElement() {
    if (this._observed === undefined) {
      const id = this.getAttribute('observes');
      this._observed = id ? document.getElementById(id) : null;
    }
    return this._observed;
  }

  _update() {
    const el = this.observedElement;
    if (!el || el.scrollWidth <= 0) return;
    const scrollLeft = document.dir === 'rtl' ? Math.abs(el.scrollLeft) : el.scrollLeft;
    // Carousels that append pages later (data-total-items) are measured against
    // their final width, otherwise the thumb would shrink abruptly on append.
    let scrollWidth = el.scrollWidth;
    const total = parseInt(el.dataset.totalItems, 10);
    const rendered = el.items ? el.items.length : 0;
    if (total > rendered && rendered > 0) scrollWidth += (total - rendered) * (scrollWidth / rendered);
    const advancement = (scrollLeft + el.clientWidth) / scrollWidth;
    this.style.setProperty('--scroll-progress', Math.max(0, Math.min(advancement, 1)).toFixed(6));
  }
}

if (!customElements.get('scroll-carousel')) {
  customElements.define('scroll-carousel', ScrollCarousel);
  customElements.define('scroll-prev-button', ScrollPrevButton);
  customElements.define('scroll-next-button', ScrollNextButton);
  customElements.define('scroll-progress', ScrollProgress);
}

/* Featured-collection carousels ship their first 8 cards in the HTML
   (sections/featured-collection.liquid); the remaining pages are fetched from
   the Section Rendering API (?section_id=…&page=N) and appended the first time
   the shopper scrolls the carousel or presses "next". Keeps ~half of the card
   Liquid and markup off the initial page load. */
function bbInitLazyCarousels(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('[data-bb-lazy-pages]').forEach((section) => {
    if (section._bbLazyBound) return;
    const carousel = section.querySelector('scroll-carousel');
    const totalPages = parseInt(section.dataset.bbLazyPages, 10);
    const sectionId = section.dataset.sectionId;
    if (!carousel || !totalPages || !sectionId) return;
    section._bbLazyBound = true;

    let nextPage = 2;
    let pending = null;
    let done = false;

    const appendCards = (html) => {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      const source = wrapper.querySelector('scroll-carousel');
      const selector = carousel.getAttribute('selector') || '.grid__item';
      const cards = source ? Array.from(source.querySelectorAll(`:scope > ${selector}`)) : [];
      cards.forEach((card) => carousel.appendChild(card));
      carousel._items = null;
      if (window.theme && theme.reinitProductGridItem) theme.reinitProductGridItem(carousel);
      if (typeof carousel._measureOverflow === 'function') carousel._measureOverflow();
      // The end edge moved: re-arm the edge detection and show the "next" control again.
      if (carousel._scrollArea) {
        carousel._scrollArea._allowTriggerNearingEndEvent = true;
        carousel._scrollArea._allowTriggerLeavingEndEvent = false;
      }
      carousel.dispatchEvent(new CustomEvent('scroll:edge-leaving', { bubbles: true, detail: { position: 'end' } }));
      const progress = section.querySelector(`scroll-progress[observes="${carousel.id}"]`);
      if (progress && typeof progress._update === 'function') {
        progress.classList.add('is-adjusting');
        progress._update();
        window.setTimeout(() => progress.classList.remove('is-adjusting'), 300);
      }
      return cards.length;
    };

    // iOS Safari freezes or resets an overflow scroller whose content changes while a
    // touch/momentum scroll is running, so fetched cards are only appended once the
    // carousel has settled (native scrollend, or the ScrollArea polyfill's scrollend).
    let lastScrollAt = 0;
    let queuedHtml = null;
    const isScrolling = () => performance.now() - lastScrollAt < 150;
    const flush = () => {
      if (queuedHtml == null || isScrolling()) return;
      const html = queuedHtml;
      queuedHtml = null;
      const added = appendCards(html);
      nextPage += 1;
      if (!added || nextPage > totalPages + 1) done = true;
    };

    const loadNextPage = () => {
      if (done || pending || queuedHtml != null) return pending;
      const url = new URL(window.location.href);
      url.search = '';
      url.hash = '';
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', String(nextPage));
      pending = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`Carousel page request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          queuedHtml = html;
          flush();
        })
        .catch(() => {
          done = true;
        })
        .finally(() => {
          pending = null;
        });
      return pending;
    };

    carousel.addEventListener(
      'scroll',
      () => {
        lastScrollAt = performance.now();
        loadNextPage();
      },
      { passive: true },
    );
    carousel.addEventListener('scrollend', () => {
      lastScrollAt = 0;
      flush();
    });
    carousel.addEventListener('touchend', () => window.setTimeout(flush, 200), { passive: true });
    carousel.addEventListener('control:next', () => loadNextPage());
    carousel.addEventListener('scroll:edge-nearing', (event) => {
      if (event.detail && event.detail.position === 'end') loadNextPage();
    });
  });
}

document.addEventListener('DOMContentLoaded', () => bbInitLazyCarousels(document));
document.addEventListener('shopify:section:load', (event) => bbInitLazyCarousels(event.target));

/* The country-flag stylesheet (and its 81 KB sprite) is only needed once a
   currency selector is on screen: layout/theme.liquid ships it as media="print"
   and this flips it to "all" the first time a flag element becomes visible. */
function bbActivateFlagStylesheet() {
  const link = document.querySelector('link[data-bb-flags-css]');
  if (!link || link.media === 'all') return;
  const flags = document.querySelectorAll('.currency-flag');
  if (!flags.length) return;
  const activate = () => {
    link.media = 'all';
    if (observer) observer.disconnect();
  };
  if (!('IntersectionObserver' in window)) {
    activate();
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) activate();
  });
  flags.forEach((flag) => observer.observe(flag));
  // Opening the drawer/footer selectors can reveal flags without a scroll.
  document.addEventListener(
    'focusin',
    (event) => {
      if (event.target.closest && event.target.closest('.multi-selectors, .site-footer, #NavDrawer')) activate();
    },
    { passive: true },
  );
}

document.addEventListener('DOMContentLoaded', bbActivateFlagStylesheet);
document.addEventListener('shopify:section:load', bbActivateFlagStylesheet);

/* snippets/customer-account.liquid renders a plain account link plus a
   <template> holding Shopify's <shopify-account> component. The component is
   swapped in on the first user input (Shopify's account.js is loaded async
   anyway), so its ~110 requests never sit in the initial page load. If the
   element never gets defined (classic accounts), the link simply stays. */
function bbUpgradeAccountComponents() {
  if (!('customElements' in window) || !document.querySelector('template[data-bb-account-component]')) return;
  customElements.whenDefined('shopify-account').then(() => {
    document.querySelectorAll('template[data-bb-account-component]').forEach((template) => {
      const link = template.previousElementSibling;
      const node = template.content.cloneNode(true);
      if (link && link.hasAttribute('data-bb-account-link')) link.replaceWith(node);
      else template.parentNode.insertBefore(node, template);
      template.remove();
    });
  });
}

// Gesture-end, keyboard or mouse movement only: swapping DOM in the header during a touch
// scroll cancels the scroll on iOS Safari, so nothing runs on touchstart/touch pointermove.
const bbAccountInputEvents = ['pointerup', 'touchend', 'keydown', 'pointermove'];
function bbOnFirstAccountInput(event) {
  if (event.type === 'pointermove' && event.pointerType !== 'mouse') return;
  if (event.type === 'pointerup' && event.pointerType !== 'mouse') return;
  bbAccountInputEvents.forEach((name) => window.removeEventListener(name, bbOnFirstAccountInput, true));
  window.setTimeout(bbUpgradeAccountComponents, 300);
}
bbAccountInputEvents.forEach((name) => window.addEventListener(name, bbOnFirstAccountInput, { capture: true, passive: true }));
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('DOMContentLoaded', bbUpgradeAccountComponents);
  document.addEventListener('shopify:section:load', bbUpgradeAccountComponents);
}

/* Recently viewed slider — renders full product-grid-item cards (fetched per
   handle through the Section Rendering API) inside the same grid/slider markup
   as the featured collection. Reads the handle history theme.js stores in
   localStorage and prepends those cards before any merchant-pinned products.
   Sold-out products are skipped (data-hide-sold-out) and backfilled from older
   history, so the slider still fills up to its limit. */
class RecentlyViewedSlider extends HTMLElement {
  connectedCallback() {
    this.template = this.querySelector('[data-recently-viewed-template]');
    this.output = this.querySelector('[data-recently-viewed-output]');
    if (!this.template || !this.output) return;

    this.currentHandle = this.dataset.productHandle || '';
    this.currentProductId = this.dataset.productId || '';
    this.limit = parseInt(this.dataset.limit, 10) || 0;
    this.cardSection = this.dataset.cardSection;
    this.gridFrom = this.dataset.cardGridFrom;
    this.gridTo = this.dataset.cardGridTo;
    this.hasExtra = this.dataset.hasExtra === 'true';
    this.hideSoldOut = this.dataset.hideSoldOut === 'true';

    this.recent = this._readRecent();
    if (!this.recent.length && !this.hasExtra) {
      this.classList.add('hide');
      return;
    }
    this._observe();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  _readRecent() {
    let store = {};
    try {
      store = (window.theme && theme.recentlyViewed && theme.recentlyViewed.recent) || JSON.parse(localStorage.getItem('theme-recent') || '{}');
    } catch (e) {
      store = {};
    }
    // 3x the limit as headroom for the sold-out and duplicate cards _render skips.
    return Object.keys(store)
      .filter((handle) => handle && handle !== 'undefined' && (!this.currentHandle || handle !== this.currentHandle))
      .reverse()
      .slice(0, this.limit * 3)
      .map((handle) => ({ handle }));
  }

  _observe() {
    if (!('IntersectionObserver' in window)) {
      this._render();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this._render();
        }
      },
      { rootMargin: '600px' },
    );
    this.observer.observe(this);
  }

  async _render() {
    if (this.rendered) return;
    this.rendered = true;

    // Fetch in batches of the missing count: usually one batch, more only when cards were skipped.
    const cards = [];
    const seenIds = new Set();
    let next = 0;
    while (cards.length < this.limit && next < this.recent.length) {
      const batch = this.recent.slice(next, next + this.limit - cards.length);
      next += batch.length;
      const results = await Promise.all(batch.map((item) => this._fetchCard(item)));
      results.forEach((card) => {
        if (!card) return;
        // Localized handles of the same product resolve to the same card - dedupe by product ID.
        const id = card.dataset.productId;
        if (id && (id === this.currentProductId || seenIds.has(id))) return;
        if (id) seenIds.add(id);
        cards.push(card);
      });
    }

    if (!cards.length && !this.hasExtra) {
      this.classList.add('hide');
      return;
    }

    const fragment = this.template.content.cloneNode(true);
    const container = fragment.querySelector('scroll-carousel') || fragment.querySelector('.grid');
    if (container) {
      const before = container.firstElementChild;
      cards.forEach((card) => container.insertBefore(card, before));
    }

    this.output.innerHTML = '';
    this.output.appendChild(fragment);

    if (window.theme && theme.reinitProductGridItem) theme.reinitProductGridItem(this.output);
  }

  async _fetchCard(item) {
    try {
      const root = window.Shopify?.routes?.root || window.theme?.routes?.home || '/';
      const localizedRoot = root.endsWith('/') ? root : `${root}/`;
      const productUrl = `${localizedRoot}products/${encodeURIComponent(item.handle)}`;
      const response = await fetch(`${productUrl}?section_id=${this.cardSection}`);
      if (!response.ok) return null;
      const wrapper = document.createElement('div');
      wrapper.innerHTML = await response.text();
      if (this.hideSoldOut && wrapper.querySelector('[data-card-available="false"]')) return null;
      const card = wrapper.querySelector('.grid__item');
      if (card && this.gridFrom && this.gridTo && this.gridFrom !== this.gridTo) {
        card.className = card.className.replace(this.gridFrom, this.gridTo);
      }
      return card;
    } catch (e) {
      return null;
    }
  }
}

if (!customElements.get('recently-viewed-slider')) {
  customElements.define('recently-viewed-slider', RecentlyViewedSlider);
}

/* Product card quick-add (bb-custom quick shop mode). A per-card mini product
   form rendered by snippets/product-grid-item.liquid. It reuses the theme's own
   variant matching (theme.Variants) and AJAX add-to-cart (theme.AjaxProduct,
   which opens the native cart drawer). Init is deferred to the first card
   hover/focus so we don't spin up a controller for every card up-front. */
// Mirrors a chosen value into a "Größe: …" readout (EU/UK spans or plain text).
// Shared by the quick-buy panel and the PDP variant picker readouts.
function bbSyncOptionReadout(input) {
  const option = input.closest('.bb-quick-add__option');
  const wrapper = input.closest('.variant-wrapper');
  const selected = option ? option.querySelector('.bb-quick-add__selected') : wrapper ? wrapper.querySelector('[data-variant-option-label]') : null;
  if (!selected) return;

  const euEl = selected.querySelector('.bb-quick-add__eu, .variant-unit-eu');
  const ukEl = selected.querySelector('.bb-quick-add__uk, .variant-unit-uk');
  if (!euEl && !ukEl) {
    selected.textContent = input.value;
    return;
  }

  selected.querySelector('[data-selection-placeholder]')?.remove();
  selected.classList.remove('is-pending');

  const parts = input.value.split(' = ').map((part) => part.trim());
  const euPart = parts.find((part) => /^EU/i.test(part)) || parts[0];
  const ukPart = parts.find((part) => /^UK/i.test(part)) || parts[parts.length - 1];
  if (euEl) euEl.textContent = euPart;
  if (ukEl) ukEl.textContent = ukPart;
}

// Pre-selects restored option values on DETACHED markup (before it's inserted)
// so a sibling swap never paints the new product's default variant for a
// split second before snapping back to the previously chosen size - the
// value is already correct on first paint, eliminating the blink.
function bbApplyRestoredValues(root, restoreValues) {
  const matched = [];
  restoreValues.forEach(({ index, value }) => {
    const candidates = root.querySelectorAll(`[data-variant-input][data-index="${index}"]`);
    let match = null;
    candidates.forEach((candidate) => {
      const isMatch = candidate.value === value;
      candidate.checked = isMatch;
      if (isMatch) match = candidate;
    });
    if (match) {
      bbSyncOptionReadout(match);
      matched.push(match);
    }
  });
  return matched;
}

// Toggles metaobject sale badges ([data-sale-badge]) for the given variant and
// re-fills the %percent% placeholder from its compare-at reduction.
function bbUpdateSaleBadges(scope, variant) {
  scope.querySelectorAll('[data-sale-badge]').forEach((badge) => {
    const onSale = !!(variant && variant.compare_at_price > variant.price);
    badge.hidden = !onSale;
    if (!onSale) return;

    const template = badge.dataset.badgeTemplate;
    if (template && template.includes('%percent%')) {
      const percent = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
      badge.textContent = template.replaceAll('%percent%', `-${percent}%`);
    }
  });
}

function bbSetVariantSelectionState(scope, selectionComplete, variant = true) {
  scope.querySelectorAll('bb-variant-selection-gate').forEach((gate) => {
    if (typeof gate.setComplete === 'function') {
      gate.setComplete(selectionComplete);
    } else {
      gate.dataset.selectionComplete = String(selectionComplete);
    }
  });
  scope.querySelectorAll('[data-variant-dynamic-checkout]').forEach((checkout) => {
    checkout.hidden = !selectionComplete;
  });
  scope.querySelectorAll('[data-variant-selection-inventory]').forEach((inventory) => {
    inventory.classList.toggle('hide', !selectionComplete || !variant);
  });
}

class BBVariantSelectionGate extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.button = this.querySelector('[data-add-to-cart], button[type="submit"]');
    this.tooltipId = this.dataset.tooltipId;
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this.addEventListener('pointerdown', this._onPointerDown, true);
    this.addEventListener('keydown', this._onKeydown);
    this.setComplete(this.dataset.selectionComplete === 'true');
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this._onPointerDown, true);
    this.removeEventListener('keydown', this._onKeydown);
  }

  setComplete(selectionComplete) {
    this.dataset.selectionComplete = String(selectionComplete);
    this.tabIndex = selectionComplete ? -1 : 0;
    if (this.button && this.tooltipId) {
      if (selectionComplete) {
        this.button.removeAttribute('aria-describedby');
      } else {
        this.button.setAttribute('aria-describedby', this.tooltipId);
      }
    }
    if (selectionComplete && document.activeElement === this) this.blur();
  }

  _onPointerDown() {
    if (this.dataset.selectionComplete === 'false') this.focus({ preventScroll: true });
  }

  _onKeydown(event) {
    if (event.key === 'Escape') this.blur();
  }
}

if (!customElements.get('bb-variant-selection-gate')) {
  customElements.define('bb-variant-selection-gate', BBVariantSelectionGate);
}

/* Deferred quick-add: product grids ship an empty <bb-quick-add data-deferred>
   (the full picker is ~45 KB of markup per card) and fetch the form from
   sections/bb-quick-add.liquid through the Section Rendering API when the
   shopper reaches the card (pointerenter/focusin). The cards in view are
   prefetched as soon as the pointer enters their section, so the picker is
   usually already there when a card is hovered. */
const BB_QUICK_ADD_SECTION = 'bb-quick-add';
const bbQuickAddFragments = new Map();
const bbQuickAddPrefetchScopes = new WeakSet();
let bbQuickAddUid = 0;

function bbFetchQuickAddFragment(productUrl) {
  if (!bbQuickAddFragments.has(productUrl)) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', BB_QUICK_ADD_SECTION);
    const request = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Quick-add fragment request failed: ${response.status}`);
        return response.text();
      })
      .catch((error) => {
        bbQuickAddFragments.delete(productUrl);
        throw error;
      });
    bbQuickAddFragments.set(productUrl, request);
  }
  return bbQuickAddFragments.get(productUrl);
}

function bbCanHoverQuickAdd() {
  return window.matchMedia('(min-width: 769px) and (hover: hover)').matches && !document.documentElement.classList.contains('supports-touch');
}

class BBQuickAdd extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this._setup = this._setup.bind(this);
    this._onPointerEnter = (event) => {
      // Touch pointers never see the hover overlay; a fetch + DOM swap mid-swipe would only
      // risk cancelling the touch scroll on iOS.
      if (event.pointerType === 'touch') return;
      this._setup();
    };
    this._trigger = this.closest('.grid-product') || this;
    this._trigger.addEventListener('pointerenter', this._onPointerEnter);
    this._trigger.addEventListener('focusin', this._setup);
    if (this.isDeferred) this._registerPrefetchScope();
  }

  get isDeferred() {
    return this.dataset.deferred === 'true';
  }

  _registerPrefetchScope() {
    const scope = this.closest('.shopify-section') || this.closest('[data-section-id]');
    if (!scope || bbQuickAddPrefetchScopes.has(scope)) return;
    bbQuickAddPrefetchScopes.add(scope);
    scope.addEventListener(
      'pointerenter',
      () => {
        if (!bbCanHoverQuickAdd()) return;
        const inView = Array.from(scope.querySelectorAll('bb-quick-add[data-deferred="true"]')).filter((el) => {
          const rect = (el.closest('.grid-product') || el).getBoundingClientRect();
          return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
        });
        inView.slice(0, 6).forEach((el) => el.load());
      },
      { passive: true },
    );
  }

  load() {
    if (!this.isDeferred) return Promise.resolve(true);
    if (this._loading) return this._loading;
    const productUrl = this.dataset.quickAddUrl;
    if (!productUrl) return Promise.resolve(false);

    this._loading = bbFetchQuickAddFragment(productUrl)
      .then((html) => {
        if (!this.isConnected) return false;
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        const fetched = wrapper.querySelector('bb-quick-add');
        if (!fetched) throw new Error('Quick-add fragment is incomplete');

        // The same product can sit in several carousels: give every copy unique input ids/names.
        bbQuickAddUid += 1;
        this.innerHTML = fetched.innerHTML.split(`bb-qa-${BB_QUICK_ADD_SECTION}-`).join(`bb-qa-c${bbQuickAddUid}-`);
        ['data-product-id', 'data-product-available', 'data-require-explicit-selection'].forEach((name) => {
          if (fetched.hasAttribute(name)) this.setAttribute(name, fetched.getAttribute(name));
          else this.removeAttribute(name);
        });
        delete this.dataset.deferred;
        this.removeAttribute('aria-busy');
        this.classList.remove('bb-quick-add--deferred');
        const carousel = this.closest('scroll-carousel');
        if (carousel && typeof carousel._measureOverflow === 'function') carousel._measureOverflow();
        return true;
      })
      .catch(() => {
        // Leave the placeholder in place; the next hover/focus retries.
        this._loading = null;
        return false;
      });
    return this._loading;
  }

  _setup() {
    if (this._initialised) return;
    if (this.isDeferred) {
      this.load().then((loaded) => {
        if (loaded) this._setup();
      });
      return;
    }
    if (!window.theme || !theme.Variants || !theme.AjaxProduct) return;

    this.form = this.querySelector('.bb-quick-add__form');
    if (!this.form) return;

    let variants = [];
    const dataEl = this.querySelector('.bb-quick-add__data');
    if (dataEl) {
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (error) {
        return;
      }
    }

    this._initialised = true;
    this._trigger.removeEventListener('pointerenter', this._onPointerEnter);
    this._trigger.removeEventListener('focusin', this._setup);

    this.button = this.form.querySelector('[data-add-to-cart]');
    this.buttonText = this.form.querySelector('[data-add-to-cart-text]');

    this.variantHandler = new theme.Variants({
      container: this.form,
      variants: variants,
      singleOptionSelector: '[data-variant-input]',
      originalSelectorId: '[data-product-select]',
      enableHistoryState: false,
      requireExplicitSelection: this.dataset.requireExplicitSelection === 'true',
      explicitSelectionComplete: false,
    });

    this.ajaxProduct = new theme.AjaxProduct(this.form);

    this.form.addEventListener('variantChange', (event) => {
      this._updateButton(event.detail.variant, event.detail.selectionComplete);
      this._updateCardPrice(event.detail.variant);
    });

    this.form.addEventListener('change', (event) => {
      const input = event.target.closest('[data-variant-input]');
      if (input) this._syncHeader(input);
    });
    this.form.addEventListener('click', (event) => this._onSiblingClick(event));

    this._updateButton(this.variantHandler.currentVariant, this.variantHandler.isSelectionComplete());
  }

  _onSiblingClick(event) {
    const link = event.target.closest('[data-sibling-product-url]');
    const card = this.closest('.grid-product--bb-quick');
    if (!link || !this.contains(link) || !card) return;

    event.preventDefault();
    this._swapCardProduct(link, card);
  }

  async _swapCardProduct(link, card) {
    const targetUrl = new URL(link.href, window.location.origin);
    const sectionUrl = new URL(targetUrl);
    sectionUrl.searchParams.set('section_id', 'recently-viewed-card');
    const restoreValues = Array.from(this.form.querySelectorAll('[data-variant-input]:checked')).map((input) => ({ index: input.dataset.index, value: input.value }));
    const restoreFocus = link.matches(':focus-visible');

    this._siblingAbortController?.abort();
    this._siblingAbortController = new AbortController();
    card.classList.add('is-loading-sibling');
    card.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(sectionUrl, { signal: this._siblingAbortController.signal });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);

      const wrapper = document.createElement('div');
      wrapper.innerHTML = await response.text();
      const fetchedCard = wrapper.querySelector('.grid-product');
      const nextContent = fetchedCard?.querySelector('.grid-product__content');
      const currentContent = card.querySelector(':scope > .grid-product__content');
      if (!fetchedCard || !nextContent || !currentContent) throw new Error('Product card fragment is incomplete');

      card.dataset.productHandle = fetchedCard.dataset.productHandle || '';
      card.dataset.productId = fetchedCard.dataset.productId || '';

      const restoredInputs = bbApplyRestoredValues(nextContent, restoreValues);
      currentContent.replaceWith(nextContent);
      card.classList.remove('is-loading-sibling');
      card.removeAttribute('aria-busy');

      if (window.theme?.reinitProductGridItem) theme.reinitProductGridItem(card);
      card.dispatchEvent(new Event('pointerenter'));

      // Values are already checked (pre-insertion) - dispatch only to sync price/availability/button state.
      restoredInputs.forEach((input) => input.dispatchEvent(new Event('change', { bubbles: true })));

      if (restoreFocus) {
        card.querySelector('bb-quick-add [data-variant-input]:checked')?.focus({ preventScroll: true });
      }

      card.closest('scroll-carousel')?._measureOverflow();
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(targetUrl.toString());
    }
  }

  _syncHeader(input) {
    bbSyncOptionReadout(input);
  }

  _updateButton(variant, selectionComplete = true) {
    if (!this.button) return;

    if (this.dataset.productAvailable === 'false') {
      bbSetVariantSelectionState(this, true);
      this.button.disabled = true;
      this._setButtonText('soldText');
      return;
    }

    bbSetVariantSelectionState(this, selectionComplete);

    if (!selectionComplete) {
      this.button.disabled = true;
      this._setButtonText('defaultText');
      return;
    }

    if (!variant || !variant.available) {
      // No matching variant uses the same wording as sold out (Sioux - unify text).
      this.button.disabled = true;
      this._setButtonText('soldText');
    } else {
      this.button.disabled = false;
      this._setButtonText('defaultText');
    }
  }

  _updateCardPrice(variant) {
    const card = this.closest('.grid-product');
    if (!card) return;

    bbUpdateSaleBadges(card, variant);

    if (!variant || !window.theme?.Currency) return;
    const onSale = variant.compare_at_price > variant.price;
    let html = '';
    if (onSale) {
      if (theme.strings?.regularPrice) {
        html += `<span class="visually-hidden">${theme.strings.regularPrice}</span>`;
      }
      html += `<span class="grid-product__price--original">${theme.Currency.formatMoney(variant.compare_at_price, theme.settings.moneyFormat)}</span> `;
      if (theme.strings?.salePrice) {
        html += `<span class="visually-hidden">${theme.strings.salePrice}</span>`;
      }
    }
    html += `<span class="grid-product__price--current${onSale ? ' grid-product__price--on-sale' : ''}">${theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat)}</span>`;
    if (variant.unit_price) {
      const baseUnit = theme.Currency.getBaseUnit(variant) || '';
      html += `<div class="product__unit-price">${theme.Currency.formatMoney(variant.unit_price, theme.settings.moneyFormat)}/${baseUnit}</div>`;
    }
    card.querySelectorAll('.grid-product__price').forEach((el) => {
      el.innerHTML = html;
    });
  }

  _setButtonText(key) {
    if (this.buttonText && this.buttonText.dataset[key]) {
      this.buttonText.textContent = this.buttonText.dataset[key];
    }
  }
}

if (!customElements.get('bb-quick-add')) {
  customElements.define('bb-quick-add', BBQuickAdd);
}

/* Flickity marks the non-selected hero slides aria-hidden but leaves their
   links and buttons focusable (Lighthouse "aria-hidden-focus"). Mirror the
   attribute onto `inert` so hidden slides drop out of the tab order as well. */
const bbSlideInertObserver =
  'MutationObserver' in window
    ? new MutationObserver((records) => {
        records.forEach((record) => {
          record.target.inert = record.target.getAttribute('aria-hidden') === 'true';
        });
      })
    : null;

function bbSyncSlideInert() {
  document.querySelectorAll('.slideshow__slide').forEach((slide) => {
    slide.inert = slide.getAttribute('aria-hidden') === 'true';
    if (bbSlideInertObserver && !slide._bbInertObserved) {
      slide._bbInertObserved = true;
      bbSlideInertObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
  });
}

document.addEventListener('DOMContentLoaded', bbSyncSlideInert);
document.addEventListener('page:loaded', bbSyncSlideInert);
document.addEventListener('shopify:section:load', bbSyncSlideInert);

/* Hidden hero slides ship their image as data-src/data-srcset
   (sections/slideshow.liquid). Restore them ~2 s before the autoplay switch,
   on the first interaction with the slideshow, or as soon as a slide is
   revealed, so the initial load only carries the visible slide. */
function bbDeferHeroSlides(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('.hero[data-slide-count]').forEach((hero) => {
    if (hero._bbSlidesBound) return;
    const images = Array.from(hero.querySelectorAll('img[data-src], img[data-srcset]'));
    if (!images.length) return;
    hero._bbSlidesBound = true;

    const restore = (img) => {
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        delete img.dataset.srcset;
      }
      if (img.dataset.src) {
        img.src = img.dataset.src;
        delete img.dataset.src;
      }
    };
    const restoreAll = () => images.forEach(restore);

    const wrapper = hero.closest('.slideshow-wrapper') || hero;
    // Gesture end only (touchend, not touchstart): changing image sources mid-swipe can
    // cancel the scroll on iOS Safari.
    ['pointerup', 'touchend', 'keydown', 'focusin'].forEach((name) => {
      wrapper.addEventListener(name, restoreAll, { once: true, passive: true, capture: true });
    });
    if (hero.dataset.autoplay === 'true') {
      const speed = parseInt(hero.dataset.speed, 10) || 7000;
      window.setTimeout(restoreAll, Math.max(speed - 2000, 500));
    }
    if ('MutationObserver' in window) {
      const observer = new MutationObserver(() => {
        hero.querySelectorAll('.slideshow__slide:not([aria-hidden="true"]) img[data-src], .slideshow__slide.is-selected img[data-src]').forEach(restore);
      });
      hero.querySelectorAll('.slideshow__slide').forEach((slide) => observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden', 'class'] }));
    }
  });
}

document.addEventListener('DOMContentLoaded', () => bbDeferHeroSlides(document));
document.addEventListener('shopify:section:load', (event) => bbDeferHeroSlides(event.target));
document.addEventListener('shopify:block:select', () => bbDeferHeroSlides(document));

/* Sharper hero slides once the page has loaded (Launchpad #27). The first
   paint uses the lighter q75 sources so the LCP download stays short;
   sections/slideshow.liquid puts the q85 srcset in data-hq-srcset. After load
   and an idle callback, each rendered slide image is fetched off-screen and
   swapped in once decoded, so nothing flickers. sizes stays the same, so the
   swap is not a larger LCP candidate. Slides that bbDeferHeroSlides still
   holds back get the sharp srcset for when they are restored. */
function bbUpgradeHeroImages(root) {
  if (navigator.connection && navigator.connection.saveData) return;
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('img[data-hq-srcset]').forEach((img) => {
    // The desktop/mobile twin hidden at this breakpoint (display: none) keeps its small source
    if (!img.getClientRects().length) return;
    const srcset = img.dataset.hqSrcset;
    delete img.dataset.hqSrcset;
    if (img.dataset.srcset) {
      img.dataset.srcset = srcset;
      return;
    }
    const probe = new Image();
    probe.sizes = img.sizes;
    probe.srcset = srcset;
    probe
      .decode()
      .then(() => {
        img.srcset = srcset;
      })
      .catch(() => {});
  });
}

function bbWhenPageIdle(callback) {
  const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(callback, { timeout: 3000 }) : window.setTimeout(callback, 200));
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
}

bbWhenPageIdle(() => bbUpgradeHeroImages(document));
document.addEventListener('shopify:section:load', (event) => bbUpgradeHeroImages(event.target));

// PDP gallery sale badges follow the selected variant. theme.Variants only
// dispatches a non-bubbling 'variantChange' on the section container, so each
// product section is bound directly (and re-bound after sibling swaps).
function bbBindPdpSaleBadges() {
  document.querySelectorAll('[data-section-type="product"]').forEach((container) => {
    if (container.dataset.bbSaleBadgesBound) return;
    container.dataset.bbSaleBadgesBound = 'true';
    container.addEventListener('variantChange', (event) => {
      bbUpdateSaleBadges(container, event.detail.variant);
    });
  });
}

function bbBindPdpVariantSelection() {
  document.querySelectorAll('[data-section-type="product"][data-require-explicit-selection="true"]').forEach((container) => {
    if (container.dataset.bbVariantSelectionBound) return;
    container.dataset.bbVariantSelectionBound = 'true';
    container.addEventListener('variantChange', (event) => {
      container.dataset.bbSelectedVariantId = event.detail.variant?.id || '';
      bbSetVariantSelectionState(container, event.detail.selectionComplete !== false, event.detail.variant);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bbBindPdpSaleBadges();
    bbBindPdpVariantSelection();
  });
} else {
  bbBindPdpSaleBadges();
  bbBindPdpVariantSelection();
}
document.addEventListener('shopify:section:load', () => {
  bbBindPdpSaleBadges();
  bbBindPdpVariantSelection();
});

let bbPdpSwapAbortController;

document.addEventListener('click', async (event) => {
  const link = event.target.closest('[data-sibling-product-url]');
  if (!link || link.closest('bb-quick-add')) return;

  const productSection = link.closest('[data-section-type="product"]');
  if (!productSection || window.Shopify?.designMode) return;

  event.preventDefault();
  const targetUrl = new URL(link.href, window.location.origin);
  const sectionId = productSection.dataset.sectionId;
  const previousProductTitle = productSection.dataset.productTitle;
  const sectionUrl = new URL(targetUrl);
  sectionUrl.searchParams.set('section_id', sectionId);
  const restoreValues = Array.from(productSection.querySelectorAll('[data-variant-input]:checked')).map((input) => ({ index: input.dataset.index, value: input.value }));
  const restoreFocus = link.matches(':focus-visible');

  bbPdpSwapAbortController?.abort();
  bbPdpSwapAbortController = new AbortController();
  productSection.classList.add('is-loading-sibling');
  productSection.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(sectionUrl, { signal: bbPdpSwapAbortController.signal });
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const nextSection = documentFragment.querySelector('[data-section-type="product"]');
    if (!nextSection) throw new Error('Product section fragment is incomplete');

    const restoredInputs = bbApplyRestoredValues(nextSection, restoreValues);

    const oldInstance = window.theme?.sections?._removeInstance(sectionId);
    if (oldInstance?.onUnload) oldInstance.onUnload();
    productSection.replaceWith(nextSection);
    window.theme?.sections?._onSectionLoad(null, nextSection, sectionId);
    window.theme?.sections?._findInstance(sectionId)?.updateModalProductInventory?.();
    bbBindPdpSaleBadges();
    bbBindPdpVariantSelection();

    const nextProductTitle = nextSection.dataset.productTitle;
    if (previousProductTitle && nextProductTitle && document.title.includes(previousProductTitle)) {
      document.title = document.title.replace(previousProductTitle, nextProductTitle);
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = targetUrl.toString();

    // Values are already checked (pre-insertion) - dispatch only to sync price/availability/button state.
    restoredInputs.forEach((input) => input.dispatchEvent(new Event('change', { bubbles: true })));

    if (restoreFocus) {
      const focusTarget = nextSection.querySelector('[data-variant-input]:checked') || nextSection.querySelector('[data-sibling-product-url]');
      focusTarget?.focus({ preventScroll: true });
    }

    const restoredVariantId = nextSection.dataset.bbSelectedVariantId || nextSection.querySelector('[data-product-select]')?.value;
    const selectionComplete = nextSection.querySelector('bb-variant-selection-gate')?.dataset.selectionComplete === 'true';
    if (selectionComplete && restoredVariantId) targetUrl.searchParams.set('variant', restoredVariantId);
    window.history.replaceState({}, '', `${targetUrl.pathname}${targetUrl.search}`);
    window.Shopify?.PaymentButton?.init?.();
  } catch (error) {
    if (error.name === 'AbortError') return;
    window.location.assign(targetUrl.toString());
  }
});

/* Cross-selling element (PDP block + cart drawer): fetches one recommended
   product (Section Rendering via the recommendations endpoint), falls back to
   the pinned product, and opens a quick-add popup for multi-variant products.
   With data-source="cart" the source product is derived from the cart contents
   (most recently added item), products already in the cart are skipped, and
   the recommendation refreshes on cart updates. */
class BBCrossSell extends HTMLElement {
  connectedCallback() {
    if (this._loaded) return;
    this._loaded = true;
    this.output = this.querySelector('[data-cross-sell-output]');
    if (!this.output) return;

    // In the cart drawer the element lives inside the cart <form>: keep inner
    // add-to-cart submits from bubbling into it (terms alert, loading state).
    this.addEventListener('submit', (event) => event.stopPropagation());

    if (this.dataset.source === 'cart') {
      this._onCartChange = () => {
        clearTimeout(this._reloadTimer);
        this._reloadTimer = setTimeout(() => this._load(), 150);
      };
      document.addEventListener('cart:updated', this._onCartChange);
      document.addEventListener('ajaxProduct:added', this._onCartChange);
    }

    this._load();
  }

  async _load() {
    if (this._loading) {
      this._queued = true;
      return;
    }
    this._loading = true;

    const isCart = this.dataset.source === 'cart';
    let url = this.dataset.url || '';
    let cartProductIds = null;

    if (isCart) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      try {
        const cartResponse = await fetch(root + 'cart.js');
        const cartData = await cartResponse.json();
        cartProductIds = cartData.items.map((item) => item.product_id);
      } catch (error) {
        cartProductIds = [];
      }

      if (!cartProductIds.length) {
        this._render('', null);
        this._finishLoad();
        return;
      }

      if (url) url += '&product_id=' + cartProductIds[0];
    }

    /* Content is injected as DOM NODES (not an innerHTML string): the cart
       drawer output sits inside the cart <form>, where the fragment parser
       would silently drop the nested product <form> tags. */
    let node = null;
    let productId = null;

    if (url) {
      try {
        const response = await fetch(url);
        if (response.ok) {
          const wrapper = document.createElement('div');
          wrapper.innerHTML = await response.text();

          if (isCart) {
            const choices = wrapper.querySelectorAll('[data-cross-sell-choice]');
            for (const choice of choices) {
              const choiceId = parseInt(choice.dataset.productId, 10);
              if (!cartProductIds.includes(choiceId)) {
                node = choice;
                productId = choiceId;
                break;
              }
            }
          } else {
            const content = wrapper.querySelector('[data-cross-sell-content]');
            if (content && content.firstElementChild) {
              const fragment = document.createDocumentFragment();
              while (content.firstChild) fragment.appendChild(content.firstChild);
              node = fragment;
            }
          }
        }
      } catch (error) {
        node = null;
      }
    }

    if (!node) {
      const fallback = this.querySelector('template[data-cross-sell-fallback]');
      if (fallback) {
        const fragment = fallback.content.cloneNode(true);
        const choice = fragment.querySelector('[data-cross-sell-choice]');
        const fallbackId = choice ? parseInt(choice.dataset.productId, 10) : null;
        if (!isCart || !fallbackId || !cartProductIds.includes(fallbackId)) {
          node = fragment;
          productId = fallbackId;
        }
      }
    }

    this._render(node, productId);
    this._finishLoad();
  }

  _finishLoad() {
    this._loading = false;
    if (this._queued) {
      this._queued = false;
      this._load();
    }
  }

  _render(node, productId) {
    const container = this.closest('[data-cross-sell-container], .product-block');

    if (!node) {
      this._close();
      this.output.innerHTML = '';
      this._currentProductId = null;
      if (container) container.hidden = true;
      return;
    }

    // Same product as before (e.g. a quantity-only cart update): keep the DOM.
    if (productId && productId === this._currentProductId && this.output.firstChild) {
      if (container) container.hidden = false;
      return;
    }

    this._close();
    this._currentProductId = productId;
    this.output.innerHTML = '';
    this.output.appendChild(node);
    this._wire();
    if (container) container.hidden = false;
  }

  _wire() {
    this.toggle = this.querySelector('[data-cross-sell-toggle]');
    this.popup = this.querySelector('[data-cross-sell-popup]');

    const directForm = this.querySelector('.bb-cross-sell__form');
    if (directForm && window.theme && theme.AjaxProduct) {
      new theme.AjaxProduct(directForm);
    }

    const item = this.querySelector('.bb-cross-sell__item');
    const trigger = item && item.querySelector('[data-cross-sell-add], [data-cross-sell-toggle]');
    if (item && trigger) {
      item.addEventListener('click', (event) => {
        if (event.target.closest('a, button, form')) return;
        trigger.click();
      });
    }

    if (!this.toggle || !this.popup) return;

    this._onDocClick = (event) => {
      if (this.popup.hidden || this.contains(event.target)) return;
      this._close();
    };
    this._onKeydown = (event) => {
      if (event.key === 'Escape') this._close();
    };
    this._onReposition = () => this._updatePlacement();

    this.toggle.addEventListener('click', () => {
      if (this.popup.hidden) {
        this._open();
      } else {
        this._close();
      }
    });

    const close = this.querySelector('[data-cross-sell-close]');
    if (close) close.addEventListener('click', () => this._close());

    const form = this.popup.querySelector('.bb-quick-add__form');
    if (form) form.addEventListener('ajaxProduct:added', () => this._close());
  }

  /* Desktop: show the popup above the trigger when it would overflow the
     viewport bottom, otherwise below. Re-evaluated on scroll/resize. */
  _updatePlacement() {
    if (!this.popup || this.popup.hidden) return;
    if (window.matchMedia('(max-width: 768px)').matches) {
      this.popup.classList.remove('bb-cross-sell__popup--above');
      return;
    }
    const anchor = this.popup.offsetParent || this;
    const anchorRect = anchor.getBoundingClientRect();
    const overflowsBelow = anchorRect.bottom + 8 + this.popup.offsetHeight > window.innerHeight;
    this.popup.classList.toggle('bb-cross-sell__popup--above', overflowsBelow);
  }

  _open() {
    this.popup.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    const quickAdd = this.popup.querySelector('bb-quick-add');
    if (quickAdd) quickAdd.dispatchEvent(new Event('pointerenter'));
    this._updatePlacement();
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKeydown);
    window.addEventListener('scroll', this._onReposition, { passive: true });
    window.addEventListener('resize', this._onReposition);
  }

  _close() {
    if (!this.popup || this.popup.hidden) return;
    this.popup.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('scroll', this._onReposition, { passive: true });
    window.removeEventListener('resize', this._onReposition);
  }
}

if (!customElements.get('bb-cross-sell')) {
  customElements.define('bb-cross-sell', BBCrossSell);
}

/* Size format (EU / UK) persistence + unit toggles */
const BBSizeFormat = new (class {
  set(format) {
    if (format !== 'eu' && format !== 'uk') return;
    if (this.format === format) return;
    this.format = format;
    document.dispatchEvent(new CustomEvent('sizeformat:change', { detail: { format } }));
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this._save(format), 300);
  }

  _save(format) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _size_format: format } }),
    }).catch(() => {});
  }
})();

class BBUnitToggle extends HTMLElement {
  connectedCallback() {
    this._onClick = this._onClick.bind(this);
    this._onFormatChange = this._onFormatChange.bind(this);
    this.addEventListener('click', this._onClick);
    document.addEventListener('sizeformat:change', this._onFormatChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('sizeformat:change', this._onFormatChange);
  }

  _onClick(event) {
    const tab = event.target.closest('button[data-unit]');
    if (!tab || !this.contains(tab)) return;
    this._apply(tab.dataset.unit);
    BBSizeFormat.set(tab.dataset.unit);
  }

  _onFormatChange(event) {
    this._apply(event.detail.format);
  }

  _apply(unit) {
    const group = this.closest('[data-unit-group], .bb-quick-add__option') || this;
    group.dataset.unit = unit;
    this.querySelectorAll('button[data-unit]').forEach((button) => {
      const active = button.dataset.unit === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }
}

if (!customElements.get('bb-unit-toggle')) {
  customElements.define('bb-unit-toggle', BBUnitToggle);
}

/* Variant picker selected-value readout (non-color options) */
document.addEventListener('change', (event) => {
  const input = event.target.closest('.variant-wrapper[data-type="button"] input[data-variant-input]');
  if (!input) return;
  bbSyncOptionReadout(input);
});

/* Tabs (Passform section and generic use) */
class XTabs extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.syncTargets = Array.from(this.querySelectorAll('[data-tab-sync]'));
    if (!this.tablist || this.tabs.length === 0) return;

    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this.tablist.addEventListener('click', this._onClick);
    this.tablist.addEventListener('keydown', this._onKeydown);

    let initial = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    if (initial < 0) initial = 0;
    this._select(initial, false);
  }

  disconnectedCallback() {
    this.tablist?.removeEventListener('click', this._onClick);
    this.tablist?.removeEventListener('keydown', this._onKeydown);
  }

  get selectedIndex() {
    return this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
  }

  _onClick(event) {
    const tab = event.target.closest('[role="tab"]');
    const index = this.tabs.indexOf(tab);
    if (index < 0) return;
    this._select(index, true);
  }

  _onKeydown(event) {
    const lastIndex = this.tabs.length - 1;
    const current = Math.max(this.selectedIndex, 0);
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = current === lastIndex ? 0 : current + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current === 0 ? lastIndex : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    this._select(next, true);
    this.tabs[next].focus();
  }

  _select(index, animate) {
    if (animate && index === this.selectedIndex) return;

    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });

    this.panels.forEach((panel, i) => {
      const selected = i === index;
      panel.hidden = !selected;
      if (selected && animate) this._reveal(panel);
    });

    this.syncTargets.forEach((target) => {
      target.hidden = parseInt(target.dataset.tabSync, 10) !== index;
    });
  }

  _reveal(panel) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof panel.animate !== 'function') return;
    panel.animate(
      [
        { opacity: 0, transform: 'translateY(8px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 300, easing: 'cubic-bezier(0.26, 0.54, 0.32, 1)' },
    );
  }
}

if (!customElements.get('x-tabs')) {
  customElements.define('x-tabs', XTabs);
}

/* View more text — collapses long rich text to a configurable max height with a
   gradient fade and a "show more / show less" toggle. The collapsed height comes
   from the CSS custom property --tvm-max-height (set responsively by the section).
   The toggle and fade only appear when the content actually overflows. */
class ViewMoreText extends HTMLElement {
  connectedCallback() {
    this.clip = this.querySelector('[data-clip]');
    this.content = this.querySelector('[data-content]');
    this.toggle = this.querySelector('[data-toggle]');
    this.label = this.querySelector('[data-label]');
    this.moreLabel = this.dataset.moreLabel || '';
    this.lessLabel = this.dataset.lessLabel || '';
    if (!this.clip || !this.content || !this.toggle) return;

    this.expanded = false;
    this._onClick = this._onClick.bind(this);
    this._evaluate = this._evaluate.bind(this);
    this.toggle.addEventListener('click', this._onClick);

    this.classList.add('is-ready');
    this._evaluate();

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(this._evaluate);
      this._ro.observe(this.content);
    } else {
      window.addEventListener('resize', this._evaluate);
    }
  }

  disconnectedCallback() {
    if (this._ro) this._ro.disconnect();
    else window.removeEventListener('resize', this._evaluate);
    if (this.toggle) this.toggle.removeEventListener('click', this._onClick);
  }

  _evaluate() {
    if (this.expanded) return;
    const overflowing = this.content.scrollHeight - this.clip.clientHeight > 2;
    this.classList.toggle('is-overflowing', overflowing);
  }

  _onClick() {
    this.expanded = !this.expanded;
    this.classList.toggle('is-expanded', this.expanded);
    this.toggle.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
    if (this.label) this.label.textContent = this.expanded ? this.lessLabel : this.moreLabel;
  }
}

if (!customElements.get('view-more-text')) {
  customElements.define('view-more-text', ViewMoreText);
}

/* Collection chips — proxy the theme's own collection filter checkboxes so a
   chip click re-renders the grid via AJAX (theme.Collection) instead of a full
   page load. Each chip carries data-chip-filters="name=value&name=value". On
   click we toggle the matching <input.tag__input> in the .filter-form and fire a
   bubbling "input" event, which the theme picks up to run the AJAX render (it
   also handles the URL, active-tag chips and grid re-init). If a matching
   checkbox/form isn't present (no facet, or non-collection page), we do nothing
   and let the chip's href navigate normally (server-rendered fallback). */
function collectionChipsParsePairs(raw) {
  const pairs = [];
  if (!raw) return pairs;
  raw.split('&').forEach((part) => {
    if (!part) return;
    const idx = part.indexOf('=');
    if (idx === -1) return;
    let name = part.slice(0, idx);
    let value = part.slice(idx + 1);
    try {
      name = decodeURIComponent(name.replace(/\+/g, ' '));
    } catch (error) {
      /* keep raw name */
    }
    try {
      value = decodeURIComponent(value.replace(/\+/g, ' '));
    } catch (error) {
      /* keep raw value */
    }
    pairs.push({ name, value });
  });
  return pairs;
}

function collectionChipsFindCheckbox(pair) {
  const inputs = document.querySelectorAll('.filter-form input.tag__input');
  for (let i = 0; i < inputs.length; i++) {
    if (inputs[i].name === pair.name && inputs[i].value === pair.value) {
      return inputs[i];
    }
  }
  return null;
}

class CollectionChips extends HTMLElement {
  connectedCallback() {
    this._onClick = this._onClick.bind(this);
    this._sync = this._sync.bind(this);

    this.addEventListener('click', this._onClick);
    document.addEventListener('collection:reloaded', this._sync);
    this._sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('collection:reloaded', this._sync);
  }

  _onClick(event) {
    const chip = event.target.closest('.collection-chip[data-chip-filters]');
    if (!chip || !this.contains(chip)) return;

    const pairs = collectionChipsParsePairs(chip.getAttribute('data-chip-filters'));
    if (!pairs.length) return;

    const boxes = [];
    for (let i = 0; i < pairs.length; i++) {
      const box = collectionChipsFindCheckbox(pairs[i]);
      if (!box) return;
      boxes.push(box);
    }

    event.preventDefault();

    const allChecked = boxes.every((box) => box.checked);
    boxes.forEach((box) => {
      box.checked = !allChecked;
    });

    boxes[boxes.length - 1].dispatchEvent(new Event('input', { bubbles: true }));
  }

  _sync() {
    const params = new URLSearchParams(window.location.search);
    this.querySelectorAll('.collection-chip[data-chip-filters]').forEach((chip) => {
      const pairs = collectionChipsParsePairs(chip.getAttribute('data-chip-filters'));
      if (!pairs.length) return;
      let active = true;
      for (let i = 0; i < pairs.length; i++) {
        if (params.getAll(pairs[i].name).indexOf(pairs[i].value) === -1) {
          active = false;
          break;
        }
      }
      chip.setAttribute('data-active', active ? 'true' : 'false');
    });
  }
}

if (!customElements.get('collection-chips')) {
  customElements.define('collection-chips', CollectionChips);
}

/* Hero video play/pause control (sections/hero-video.liquid). Toggles the
   background video across mp4 / YouTube / Vimeo players without touching
   theme.js. mp4 is controlled directly; YouTube/Vimeo via iframe postMessage. */
class HeroVideoControls extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.video-parent-section');
    this.button = this.querySelector('[data-hero-pause]');
    this.label = this.querySelector('[data-hero-pause-label]');
    this.videoType = this.dataset.videoType;
    this.isPaused = false;

    if (!this.section || !this.button) return;

    this._onClick = this._onClick.bind(this);
    this.button.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this._onClick);
  }

  _onClick() {
    this.isPaused = !this.isPaused;
    this._send(this.isPaused ? 'pause' : 'play');
    this.classList.toggle('is-paused', this.isPaused);
    this.button.setAttribute('aria-pressed', String(this.isPaused));

    const nextLabel = this.isPaused ? this.button.dataset.labelPlay : this.button.dataset.labelPause;
    if (nextLabel) {
      this.button.setAttribute('aria-label', nextLabel);
      if (this.label) this.label.textContent = nextLabel;
    }
  }

  _send(action) {
    if (this.videoType === 'mp4') {
      const video = this.section.querySelector('video');
      if (!video) return;
      if (action === 'pause') {
        video.pause();
      } else {
        const playPromise = video.play();
        if (playPromise && playPromise.catch) playPromise.catch(function () {});
      }
      return;
    }

    const iframe = this.section.querySelector('iframe');
    if (!iframe || !iframe.contentWindow) return;

    if (this.videoType === 'youtube') {
      iframe.contentWindow.postMessage(
        JSON.stringify({
          event: 'command',
          func: action === 'pause' ? 'pauseVideo' : 'playVideo',
          args: '',
        }),
        '*',
      );
    } else if (this.videoType === 'vimeo') {
      iframe.contentWindow.postMessage(JSON.stringify({ method: action === 'pause' ? 'pause' : 'play' }), '*');
    }
  }
}

if (!customElements.get('hero-video-controls')) {
  customElements.define('hero-video-controls', HeroVideoControls);
}

/* Collection grid promo video — adds an accessible play/pause toggle (BFSG)
   to the native, autoplaying, muted promo video in the collection grid. */
class PromoVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.controls = this.querySelector('.collection-promo__controls');
    this.button = this.querySelector('[data-promo-pause]');
    this.label = this.querySelector('[data-promo-pause-label]');
    this.isPaused = false;

    if (!this.video || !this.button) return;

    this._onClick = this._onClick.bind(this);
    this.button.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this._onClick);
  }

  _onClick() {
    this.isPaused = !this.isPaused;

    if (this.isPaused) {
      this.video.pause();
    } else {
      const playPromise = this.video.play();
      if (playPromise && playPromise.catch) playPromise.catch(function () {});
    }

    if (this.controls) this.controls.classList.toggle('is-paused', this.isPaused);
    this.button.setAttribute('aria-pressed', String(this.isPaused));

    const nextLabel = this.isPaused ? this.button.dataset.labelPlay : this.button.dataset.labelPause;
    if (nextLabel) {
      this.button.setAttribute('aria-label', nextLabel);
      if (this.label) this.label.textContent = nextLabel;
    }
  }
}

if (!customElements.get('promo-video')) {
  customElements.define('promo-video', PromoVideo);
}

/* Wishlist */

class BBWishlistManager {
  static ATTRIBUTE_KEY = '_bb_wishlist';
  static DEBOUNCE_MS = 300;

  constructor() {
    this.handles = [];
    this.initialized = false;
    this.initPromise = null;
    this.debounceTimer = null;
    this.removedSinceSync = new Set();
  }

  get root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  init() {
    if (this.initialized) return Promise.resolve(this.handles);
    if (this.initPromise) return this.initPromise;

    this.initPromise = fetch(`${this.root}cart.js`)
      .then((res) => res.json())
      .then((cart) => {
        const raw = cart.attributes?.[BBWishlistManager.ATTRIBUTE_KEY] || '';
        this.handles = raw ? raw.split(',').filter(Boolean) : [];
        this.initialized = true;
        this.dispatch();
        return this.handles;
      })
      .catch(() => {
        this.handles = [];
        this.initialized = true;
        return this.handles;
      });

    return this.initPromise;
  }

  save() {
    const removals = new Set(this.removedSinceSync);
    this.removedSinceSync.clear();

    return fetch(`${this.root}cart.js`)
      .then((res) => res.json())
      .then((cart) => {
        const raw = cart.attributes?.[BBWishlistManager.ATTRIBUTE_KEY] || '';
        const serverHandles = raw ? raw.split(',').filter(Boolean) : [];
        const merged = [...new Set([...serverHandles, ...this.handles])];
        this.handles = merged.filter((handle) => !removals.has(handle));

        return fetch(`${this.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { [BBWishlistManager.ATTRIBUTE_KEY]: this.handles.join(',') } }),
        });
      })
      .then(() => this.dispatch())
      .catch(() => {});
  }

  dispatch() {
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: [...this.handles] } }));
  }

  debouncedSave() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.save(), BBWishlistManager.DEBOUNCE_MS);
  }

  get() {
    return [...this.handles];
  }

  has(handle) {
    return this.handles.includes(handle);
  }

  add(handle) {
    if (!handle || this.handles.includes(handle)) return;
    this.removedSinceSync.delete(handle);
    this.handles.push(handle);
    this.dispatch();
    this.debouncedSave();
  }

  remove(handle) {
    const idx = this.handles.indexOf(handle);
    if (idx === -1) return;
    this.removedSinceSync.add(handle);
    this.handles.splice(idx, 1);
    this.dispatch();
    this.debouncedSave();
  }

  toggle(handle) {
    if (this.handles.includes(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  }
}

const BBWishlist = new BBWishlistManager();

// Header wishlist indicator dot
document.addEventListener('wishlist:change', (e) => {
  const active = e.detail.handles.length > 0;
  document.querySelectorAll('[data-wishlist-bubble]').forEach((bubble) => {
    bubble.classList.toggle('cart-link__bubble--visible', active);
  });
});
BBWishlist.init();

class BBWishlistButton extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._onWishlistChange = this._onWishlistChange.bind(this);
  }

  connectedCallback() {
    BBWishlist.init();
    this.addEventListener('click', this._onClick);
    document.addEventListener('wishlist:change', this._onWishlistChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('wishlist:change', this._onWishlistChange);
  }

  _onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const handle = this.getAttribute('handle');
    if (!handle) return;
    BBWishlist.toggle(handle);
    if (e.target instanceof Element) e.target.closest('button')?.blur();
  }

  _onWishlistChange(e) {
    this.dataset.active = e.detail.handles.includes(this.getAttribute('handle')).toString();
  }
}
if (!customElements.get('bb-wishlist-button')) {
  customElements.define('bb-wishlist-button', BBWishlistButton);
}

class BBWishlistGrid extends HTMLElement {
  constructor() {
    super();
    this._onWishlistChange = this._onWishlistChange.bind(this);
  }

  connectedCallback() {
    this._grid = this.querySelector('[data-wishlist-grid]');
    this._empty = this.querySelector('[data-wishlist-empty]');
    this._share = this.querySelector('[data-wishlist-share]');
    document.addEventListener('wishlist:change', this._onWishlistChange);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:change', this._onWishlistChange);
  }

  _onWishlistChange(e) {
    if (!this._grid) return;
    const handles = e.detail?.handles || [];
    if (this._share) this._share.hidden = handles.length === 0;
  }
}
if (!customElements.get('bb-wishlist-grid')) {
  customElements.define('bb-wishlist-grid', BBWishlistGrid);
}

class BBWishlistShareButton extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick(e) {
    e.preventDefault();
    BBWishlist.init().then((handles) => {
      if (!handles.length) return;
      const url = new URL(window.location.href);
      url.search = '';
      url.hash = '';
      url.searchParams.set('wishlist', handles.join(','));
      const shareUrl = url.toString();

      if (navigator.share) {
        navigator.share({ url: shareUrl }).catch(() => this._copyFallback(shareUrl));
      } else {
        this._copyFallback(shareUrl);
      }
    });
  }

  _copyFallback(url) {
    if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
  }
}
if (!customElements.get('bb-wishlist-share-button')) {
  customElements.define('bb-wishlist-share-button', BBWishlistShareButton);
}

/* Import a shared wishlist passed via the ?wishlist= query parameter. */
(function () {
  const params = new URLSearchParams(window.location.search);
  const shared = params.get('wishlist');
  if (shared === null) return;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const handles = shared.split(',').filter(Boolean);

  const cleanUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('wishlist');
    window.location.replace(url.toString());
  };

  if (!handles.length) {
    cleanUrl();
    return;
  }

  fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { _bb_wishlist: handles.join(',') } }),
  })
    .then(cleanUrl)
    .catch(cleanUrl);
})();

(function () {
  const TRIGGER_SELECTOR = '[data-size-converter-trigger]';
  const SOURCE_SELECTOR = 'template[data-size-converter-source]';

  function openConverter(trigger) {
    const source = document.querySelector(SOURCE_SELECTOR);
    if (!source) return;

    trigger.dispatchEvent(
      new CustomEvent('tooltip:open', {
        bubbles: true,
        detail: { context: 'size-converter', content: source.innerHTML },
      }),
    );
  }

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    event.preventDefault();
    openConverter(trigger);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    event.preventDefault();
    openConverter(trigger);
  });
})();

/* Metafield exact-match search (Artikelnummer). Fetches the All collection
   filtered by filter.p.m.<ns>.<key>=<query> through the Section Rendering API
   (sections/metafield-search-card.liquid) and shows exact matches as a
   companion block above the /search grid (<bb-metafield-search>) plus an
   extra group in the predictive dropdown. Native results and pagination stay
   untouched; products already present natively are skipped. Config comes from
   the "searchable_metafields" / "search_availability_filter_enable" theme
   settings via data attributes. */
function bbMetafieldSearchFetch({ endpoint, metafields, query, hideUnavailable, signal }) {
  const requests = metafields.map((metafield) => {
    const separator = metafield.indexOf('.');
    const namespace = metafield.slice(0, separator);
    const key = metafield.slice(separator + 1);
    if (!namespace || !key) return Promise.resolve(null);

    const params = new URLSearchParams();
    params.set(`filter.p.m.${namespace}.${key}`, query);
    if (hideUnavailable) params.set('filter.v.availability', '1');
    params.set('section_id', 'metafield-search-card');

    return fetch(`${endpoint}?${params.toString()}`, { signal })
      .then((response) => (response.ok ? response.text() : null))
      .catch((error) => {
        if (error.name === 'AbortError') throw error;
        return null;
      });
  });

  return Promise.all(requests).then((texts) => {
    const seen = new Set();
    const result = { cards: [], listItems: [], heading: '' };

    texts.forEach((text) => {
      if (!text) return;
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const rootEl = doc.querySelector('[data-metafield-search-results]');
      if (!rootEl) return;
      if (!result.heading) result.heading = rootEl.getAttribute('data-heading') || '';

      // cards + list items render the same products in the same order
      const cards = rootEl.querySelectorAll('[data-metafield-search-cards] [data-product-handle]');
      const items = rootEl.querySelectorAll('[data-metafield-search-list] [data-product-handle]');
      cards.forEach((card, index) => {
        const handle = card.getAttribute('data-product-handle');
        if (!handle || seen.has(handle)) return;
        seen.add(handle);
        result.cards.push(card);
        if (items[index]) result.listItems.push(items[index]);
      });
    });

    return result;
  });
}

function bbMetafieldSearchConfig(el) {
  return {
    endpoint: el.dataset.endpoint || el.dataset.metafieldEndpoint || `${window.Shopify?.routes?.root || '/'}collections/all`,
    metafields: (el.dataset.metafields || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean),
    hideUnavailable: el.dataset.hideUnavailable === 'true',
  };
}

/* Search page companion block (rendered by sections/main-search.liquid). */
class BBMetafieldSearch extends HTMLElement {
  connectedCallback() {
    this._output = this.querySelector('[data-metafield-search-output]');
    this._headingEl = this.querySelector('[data-metafield-search-heading]');
    this._query = (this.dataset.query || '').trim();
    this._nativeCount = parseInt(this.dataset.resultsCount, 10) || 0;
    this._currentTitle = this.dataset.titleCurrent || '';

    const { endpoint, metafields, hideUnavailable } = bbMetafieldSearchConfig(this);
    if (!this._output || !this._query || !metafields.length) return;

    this._onReloaded = () => this._dedupe();
    document.addEventListener('collection:reloaded', this._onReloaded);

    this._controller = new AbortController();
    bbMetafieldSearchFetch({
      endpoint,
      metafields,
      query: this._query,
      hideUnavailable,
      signal: this._controller.signal,
    })
      .then(({ cards, heading }) => {
        if (!cards.length) return;
        if (this._headingEl) this._headingEl.textContent = heading;
        cards.forEach((card) => this._output.appendChild(card));
        this._dedupe();
        if (window.theme?.reinitProductGridItem) theme.reinitProductGridItem(this._output);
      })
      .catch(() => {});
  }

  disconnectedCallback() {
    document.removeEventListener('collection:reloaded', this._onReloaded);
    if (this._controller) this._controller.abort();
  }

  /* Hide companion cards duplicated in the (AJAX re-rendered) native grid;
     hide the whole block when nothing unique remains. */
  _dedupe() {
    if (!this._output) return;
    const nativeHandles = new Set(Array.from(document.querySelectorAll('#CollectionAjaxContent [data-product-handle]')).map((el) => el.getAttribute('data-product-handle')));

    let visible = 0;
    this._output.querySelectorAll('[data-product-handle]').forEach((card) => {
      const duplicate = nativeHandles.has(card.getAttribute('data-product-handle'));
      card.hidden = duplicate;
      if (!duplicate) visible += 1;
    });

    this.hidden = visible === 0;
    this._updateResultCount(visible);
  }

  /* Shopify's page_title only counts native results, so <title>, breadcrumb and
     the grid counter read "0 Ergebnisse" when the product was only found via the
     metafield lookup. Add the unique companion matches to the shown number. */
  _updateResultCount(extra) {
    const total = this._nativeCount + extra;
    const one = this.dataset.titleOne || '';
    const other = this.dataset.titleOther || '';
    if (!other) return;
    const title = total === 1 && one ? one : other.replace('__COUNT__', String(total));

    if (this._currentTitle && document.title.includes(this._currentTitle)) {
      document.title = document.title.replace(this._currentTitle, title);
    }
    this._currentTitle = title;
    document.querySelectorAll('[data-search-page-title]').forEach((el) => {
      el.textContent = title;
    });

    // Grid counter ("n Ergebnisse"): re-rendered by AJAX filters, so adjust each fresh element once.
    document.querySelectorAll('[data-search-result-count]:not([data-mf-adjusted])').forEach((el) => {
      const match = el.textContent.match(/\d+/);
      if (!match) return;
      const count = parseInt(match[0], 10) + extra;
      const countOne = el.dataset.countOne || '';
      const countOther = el.dataset.countOther || '';
      if (count === 1 && countOne) {
        el.textContent = countOne;
      } else if (countOther) {
        el.textContent = countOther.replace('2', String(count));
      } else {
        el.textContent = el.textContent.replace(match[0], String(count));
      }
      el.setAttribute('data-mf-adjusted', '');
    });
  }
}

if (!customElements.get('bb-metafield-search')) {
  customElements.define('bb-metafield-search', BBMetafieldSearch);
}

/* Predictive dropdown group (event dispatched by theme.js PredictiveSearch). */
(function () {
  const controllers = new WeakMap();

  function reorderGroups(container) {
    const wrapper = container.querySelector('.predictive-search-results');
    if (!wrapper || wrapper.dataset.resultOrder === 'default') return;

    const group1 = wrapper.querySelector('.results__group-1');
    const group2 = wrapper.querySelector('.results__group-2');
    if (!group1 || !group2) return;

    const productNodes = Array.from(group2.children).filter((node) => node.matches('.results--products'));
    let collectionNodes = Array.from(group1.children).filter((node) => node.matches('.results--collections'));
    if (!collectionNodes.length) {
      collectionNodes = Array.from(group2.children).filter((node) => node.matches('.results--collections'));
    }
    const otherNodes = Array.from(group1.children).filter((node) => node.matches('.results:not(.results--collections)'));

    group2.className = 'results__group-2 results__group--ordered results__group--products';
    group2.removeAttribute('style');
    group2.replaceChildren(...productNodes);

    const collections = document.createElement('div');
    collections.className = 'results__group--ordered results__group--collections';
    collections.append(...collectionNodes);

    const other = document.createElement('div');
    other.className = 'results__group--ordered results__group--other';
    other.append(...otherNodes);

    const groups = { products: group2, collections, other };
    const orders = {
      products_first: ['products', 'collections', 'other'],
      collections_first: ['collections', 'products', 'other'],
      other_first: ['other', 'products', 'collections'],
    };

    wrapper.replaceChildren(...(orders[wrapper.dataset.resultOrder] || orders.products_first).map((name) => groups[name]));
  }

  function injectGroup(container, listItems, heading) {
    const wrapper = container.querySelector('.predictive-search-results');
    if (!wrapper) return;

    // skip products the native dropdown already shows
    const nativePaths = new Set(Array.from(wrapper.querySelectorAll('.results--products a[href]')).map((link) => link.pathname));
    const fresh = listItems.filter((item) => {
      const link = item.querySelector('a[href]');
      return link && !nativePaths.has(link.pathname);
    });
    if (!fresh.length) return;

    const group = document.createElement('div');
    group.className = 'results results--products results--metafield';

    const headingEl = document.createElement('h3');
    headingEl.className = 'h4';
    headingEl.id = 'predictive-search-metafield';
    headingEl.textContent = heading;

    const list = document.createElement('ul');
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-labelledby', 'predictive-search-metafield');
    fresh.forEach((item) => list.appendChild(item));

    group.appendChild(headingEl);
    group.appendChild(list);

    const group2 = wrapper.querySelector('.results__group-2');
    if (group2) {
      group2.insertBefore(group, group2.firstChild);
    } else {
      // no-results branch: swap the "no results" link for our matches
      const none = wrapper.querySelector('.predictive-search__no-results');
      if (none && none.closest('.results')) none.closest('.results').hidden = true;
      wrapper.classList.remove('predictive-search-results--none');
      wrapper.appendChild(group);
    }
  }

  document.addEventListener('predictive-search:rendered', (event) => {
    const el = event.target;
    if (!el || !el.dataset) return;

    const container = event.detail && event.detail.results;
    if (!container) return;
    reorderGroups(container);

    const { endpoint, metafields, hideUnavailable } = bbMetafieldSearchConfig(el);
    const query = ((event.detail && event.detail.searchTerm) || '').trim();
    if (!metafields.length || !query) return;

    const previous = controllers.get(el);
    if (previous) previous.abort();
    const controller = new AbortController();
    controllers.set(el, controller);

    bbMetafieldSearchFetch({ endpoint, metafields, query, hideUnavailable, signal: controller.signal })
      .then(({ listItems, heading }) => {
        if (!listItems.length) return;
        // stale guard: dropdown may have re-rendered for a newer term
        const input = el.querySelector('input[type="search"]');
        if (input && input.value.trim() !== query) return;
        injectGroup(container, listItems, heading);
      })
      .catch(() => {});
  });
})();

/* Article/collection rating (bb Sioux app proxy)
   Interactive widget rendered by snippets/article-rating.liquid.
   Click a star -> short grace period (correctable) -> POST /apps/bb-sioux/reviews. */
const BB_RATING_STORAGE = 'bb-rating:';
const BB_ARTICLE_RATING_DELAY = 1500;

class BBArticleRating extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;

    this._subjectType = this.dataset.subjectType || 'article';
    this._handle = this.dataset.handle;
    this._buttons = Array.from(this.querySelectorAll('[data-rating-star]'));
    this._status = this.querySelector('[data-rating-status]');
    // default fill = current average (server-rendered), shown until the user picks
    this._default = Number(this.dataset.defaultRating) || 0;
    this._selected = 0;
    this._timer = null;
    this._locked = false;

    const stored = this._storedRating();
    if (stored !== null) {
      this._selected = stored;
      this._paint(stored);
      this._lock(this.dataset.textAlready);
      return;
    }

    const input = this.querySelector('[data-rating-input]');
    if (input) {
      input.addEventListener('pointerleave', () => this._paint(this._selected || this._default));
    }
    this._buttons.forEach((button) => {
      const value = Number(button.dataset.value);
      button.addEventListener('click', () => this._select(value));
      button.addEventListener('pointerenter', () => this._preview(value));
      button.addEventListener('focus', () => this._preview(value));
      button.addEventListener('blur', () => this._paint(this._selected || this._default));
    });
  }

  disconnectedCallback() {
    clearTimeout(this._timer);
  }

  _storageKey() {
    return BB_RATING_STORAGE + this._subjectType + ':' + this._handle;
  }

  _storedRating() {
    try {
      const raw = localStorage.getItem(this._storageKey());
      if (raw === null) return null;
      const value = Number(raw);
      return Number.isInteger(value) && value >= 0 && value <= 5 ? value : null;
    } catch (error) {
      return null;
    }
  }

  _persist(value) {
    try {
      localStorage.setItem(this._storageKey(), String(value));
    } catch (error) {
      /* storage unavailable - server still enforces one vote */
    }
  }

  _preview(value) {
    if (this._locked) return;
    this._paint(value);
  }

  _paint(value) {
    this._buttons.forEach((button) => {
      button.classList.toggle('is-filled', Number(button.dataset.value) <= value);
    });
  }

  _select(value) {
    if (this._locked) return;
    this._selected = value;
    this._paint(value);
    this._setStatus(this.dataset.textSending);
    // grace period so a mis-click can be corrected before the vote is sent
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this._submit(), BB_ARTICLE_RATING_DELAY);
  }

  _submit() {
    if (this._locked || !this._selected) return;
    this._locked = true;

    fetch('/apps/bb-sioux/reviews', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subjectType: this._subjectType, handle: this._handle, rating: this._selected }),
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.responseType === 'REVIEW_ACCEPTED') {
          this._persist(this._selected);
          this._updateAggregates(result.rating, result.ratingCount);
          this._lock(this.dataset.textThanks);
        } else if (result.responseType === 'ALREADY_RATED') {
          this._persist(this._selected);
          this._lock(this.dataset.textAlready);
        } else {
          this._fail();
        }
      })
      .catch(() => this._fail());
  }

  _fail() {
    this._locked = false;
    this._setStatus(this.dataset.textError);
  }

  _lock(message) {
    this._locked = true;
    this.classList.add('is-locked');
    this._buttons.forEach((button) => {
      button.disabled = true;
    });
    this._setStatus(message);
  }

  _setStatus(message) {
    if (this._status) this._status.textContent = message || '';
  }

  /* Refresh every rating readout on the page (header stars included). */
  _updateAggregates(rating, count) {
    const clamped = Math.min(5, Math.max(0, Number(rating) || 0));
    const display = `${clamped.toFixed(1)}/5`;
    const template = count === 1 ? this.dataset.countOne : this.dataset.countOther;
    const countText = `(${(template || '%count%').replace('%count%', count)})`;

    document.querySelectorAll('.article-rating').forEach((widget) => {
      const fill = widget.querySelector('.article-rating__row--fill');
      if (fill) fill.style.width = `${clamped * 20}%`;
      const value = widget.querySelector('.article-rating__value');
      if (value) value.textContent = display;
      const countEl = widget.querySelector('.article-rating__count');
      if (countEl) countEl.textContent = countText;
    });
  }
}

if (!customElements.get('bb-article-rating')) {
  customElements.define('bb-article-rating', BBArticleRating);
}

/* Discount toast (snippets/discount-toast.liquid). A promo= or voucher= CODE
  param on any page applies the code via a background fetch of Shopify's
  /discount/ endpoint, which stores the code on the cart (discount_code cookie)
  until the cart meets its requirements. cart.js then tells us whether Shopify
  kept the code and whether it already applies:
    - kept + applicable  -> "success"
    - kept, not (yet) applicable -> "pending" (empty cart, minimum order value
      not reached, no eligible products, ...). Shopify keeps the code and
      applies it automatically once the cart qualifies, so this is NOT an
      error. cart.js does not distinguish a not-yet-applicable code from an
      unknown one, so the pending text must stay neutral.
    - not kept / request failed -> "error"
  Auto-hide pauses while hovered or focused. */
class BBDiscountToast extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;

    const params = (this.dataset.params || this.dataset.param || 'promo').split(',');
    const search = new URLSearchParams(window.location.search);
    const param = params.find((name) => search.get(name.trim())?.trim());
    if (!param) return;
    const code = search.get(param.trim()).trim();

    // Strip the matched param so a reload/shared URL doesn't re-apply and re-show.
    const url = new URL(window.location.href);
    url.searchParams.delete(param.trim());
    window.history.replaceState(window.history.state, '', url);

    this._applyDiscount(code);
  }

  async _applyDiscount(code) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const endpoint = `${root}discount/${encodeURIComponent(code)}`;
    let state = 'error';

    try {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`Discount request failed: ${response.status}`);

      const cartResponse = await fetch(`${root}cart.js`, { cache: 'no-store' });
      if (!cartResponse.ok) throw new Error(`Cart request failed: ${cartResponse.status}`);

      const cart = await cartResponse.json();
      const normalizedCode = code.toLowerCase();
      const discount = Array.isArray(cart.discount_codes)
        ? cart.discount_codes.find((entry) => typeof entry.code === 'string' && entry.code.trim().toLowerCase() === normalizedCode)
        : null;
      if (discount) state = discount.applicable === true ? 'success' : 'pending';
    } catch (error) {
      state = 'error';
    }

    this._show(code, state);
  }

  _show(code, state) {
    const content = this.querySelector('[data-discount-toast-text]');
    // No dedicated pending text configured -> reuse the success text.
    const template = this.querySelector(`template[data-discount-toast-${state}]`) || (state === 'pending' ? this.querySelector('template[data-discount-toast-success]') : null);
    if (content && template) {
      const escaped = code.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
      content.innerHTML = template.innerHTML.replaceAll('%code%', escaped);
    }
    this.dataset.state = state;
    this.setAttribute('role', state === 'error' ? 'alert' : 'status');

    this.querySelector('[data-discount-toast-close]')?.addEventListener('click', () => this._hide());

    this._duration = (parseInt(this.dataset.autohide, 10) || 0) * 1000;
    this.hidden = false;
    // Show (and start the auto-hide countdown) only once a frame is painted:
    // in a background tab the timer would otherwise expire before the toast
    // ever becomes visible.
    requestAnimationFrame(() => {
      if (this._closed) return;
      this.setAttribute('data-enabled', 'true');
      if (this._duration > 0) this._startTimer();
    });

    if (this._duration > 0) {
      this.addEventListener('pointerenter', () => clearTimeout(this._timer));
      this.addEventListener('focusin', () => clearTimeout(this._timer));
      this.addEventListener('pointerleave', () => this._startTimer());
      this.addEventListener('focusout', () => this._startTimer());
    }
  }

  disconnectedCallback() {
    clearTimeout(this._timer);
  }

  _startTimer() {
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this._hide(), this._duration);
  }

  _hide() {
    this._closed = true;
    clearTimeout(this._timer);
    this.setAttribute('data-enabled', 'false');
  }
}

if (!customElements.get('bb-discount-toast')) {
  customElements.define('bb-discount-toast', BBDiscountToast);
}

/* Timeline */
class BBTimeline extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.slider = this.querySelector('[data-timeline-slider]');
    this.slides = Array.from(this.querySelectorAll('[data-timeline-index]'));
    this.navItems = Array.from(this.querySelectorAll('[data-timeline-nav-item]'));
    this.navScroller = this.querySelector('[data-timeline-nav-scroll]');
    if (!this.slider || !this.slides.length) return;
    this.classList.add('is-ready');

    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this.navItems.forEach((item) => {
      item.addEventListener('click', () => this._select(Number(item.dataset.index)), { signal });
      item.addEventListener('keydown', (event) => this._onNavKeydown(event), { signal });
    });
    this.querySelector('[data-timeline-prev]')?.addEventListener('click', () => this._select(this.currentIndex - 1), { signal });
    this.querySelector('[data-timeline-next]')?.addEventListener('click', () => this._select(this.currentIndex + 1), { signal });
    this.addEventListener('shopify:block:select', (event) => this._onBlockSelect(event), { signal });

    this._sync(0, false);
    if (this.slides.length < 2 || !window.theme?.Slideshow) return;

    this.carousel = new theme.Slideshow(this.slider, {
      adaptiveHeight: false,
      avoidReflow: true,
      fade: true,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: true,
      callbacks: {
        onInit: (slide) => this._sync(Math.max(0, this.slides.indexOf(slide)), false),
        onChange: (index) => this._sync(index),
      },
    });
    this.carousel.slideshow.on('settle', () => {
      this.slides.forEach((slide) => slide.classList.remove('animate-out'));
    });
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this.carousel?.destroy();
    this.carousel = null;
    this._bound = false;
  }

  _select(index) {
    const count = this.slides.length;
    const nextIndex = ((index % count) + count) % count;
    if (this.carousel) {
      this.carousel.goToSlide(nextIndex);
    } else {
      this._sync(nextIndex);
    }
  }

  _sync(index, scroll = true) {
    this.currentIndex = index;
    this.slides.forEach((slide, slideIndex) => {
      const selected = slideIndex === index;
      slide.classList.toggle('is-selected', selected);
      slide.setAttribute('aria-hidden', selected ? 'false' : 'true');
    });
    this.navItems.forEach((item, itemIndex) => {
      item.setAttribute('aria-current', itemIndex === index ? 'true' : 'false');
    });

    const activeItem = this.navItems[index];
    if (scroll && activeItem && this.navScroller) {
      activeItem.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }

  _onNavKeydown(event) {
    const current = this.navItems.indexOf(event.currentTarget);
    let next = current;
    const isRtl = getComputedStyle(this).direction === 'rtl';

    if (event.key === 'ArrowRight') next = current + (isRtl ? -1 : 1);
    else if (event.key === 'ArrowLeft') next = current + (isRtl ? 1 : -1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.navItems.length - 1;
    else return;

    event.preventDefault();
    next = ((next % this.navItems.length) + this.navItems.length) % this.navItems.length;
    this.navItems[next].focus();
    this._select(next);
  }

  _onBlockSelect(event) {
    const slide = event.target.closest('[data-timeline-index]');
    if (!slide || !this.contains(slide)) return;
    this._select(Number(slide.dataset.timelineIndex));
  }
}

if (!customElements.get('bb-timeline')) {
  customElements.define('bb-timeline', BBTimeline);
}

/* #open-table deep link: opens the PDP size chart tooltip (tool-tip-trigger)
   when arriving via the product card quick-add "Größe finden" link. */
(function () {
  function openSizeChartFromHash() {
    if (window.location.hash !== '#open-table') return;
    customElements.whenDefined('tool-tip-trigger').then(() => {
      const trigger = document.querySelector('tool-tip-trigger[data-tool-tip="size-chart"]');
      if (trigger) trigger.click();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openSizeChartFromHash);
  } else {
    openSizeChartFromHash();
  }
  window.addEventListener('hashchange', openSizeChartFromHash);
})();
